import { Box, Flex, Icon, Skeleton, useToast } from "@chakra-ui/react"
import React, { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { BiMoviePlay } from "react-icons/bi"
import { CloudImage } from "../../CloudProviderTypes"
import { CloudImageCellProps } from "../../common/browser/CloudImageBrowser"
import CloudImageBrowserImage from "../../common/browser/CloudImageBrowserImage"
import CloudImageBrowserImageBroken from "../../common/browser/CloudImageBrowserImageBroken"
import { useVNZImage } from "../hooks/useVNZImage"
import { useVNZImageActions } from "../hooks/useVNZImageActions"

// How long a cell must stay in view before its thumbnail starts loading. During
// fast scrolling Virtuoso mounts then unmounts a cell in well under this window,
// which cancels the pending load — so only cells the user actually dwells on
// fetch, instead of every cell that flashes past.
const THUMBNAIL_LOAD_DELAY_MS = 400

const VNZImageSelector: React.FC<CloudImageCellProps> = ({
  id,
  onImageSelect,
  isSelected,
}) => {
  const { t } = useTranslation()
  const toast = useToast()
  const { vnzImage } = useVNZImage(id)
  const {
    isLoadingThumbnail,
    hasLoadedThumbnail,
    hasErrorLoadingThumbnail,
    thumbnailLocalUrl,
    mimeType,
  } = vnzImage || {}
  const canShowImage = hasLoadedThumbnail
  const isAVideo = mimeType?.startsWith("video/") ?? false
  const shouldShowVideoOverlay = isAVideo && canShowImage

  // The cell only mounts when Virtuoso brings it into view, so mounting itself
  // gates the fetch — no per-cell IntersectionObserver needed anymore.
  const shouldLoadThumbnail =
    vnzImage &&
    !hasLoadedThumbnail &&
    !isLoadingThumbnail &&
    !hasErrorLoadingThumbnail

  const { loadVNZImageThumbnail } = useVNZImageActions()

  useEffect(() => {
    if (!shouldLoadThumbnail) {
      return
    }
    // Defer the fetch: if the cell unmounts (scrolled past) before the delay
    // elapses, the cleanup cancels it and no request is made.
    const timeoutId = setTimeout(() => {
      loadVNZImageThumbnail(vnzImage)
    }, THUMBNAIL_LOAD_DELAY_MS)

    return () => clearTimeout(timeoutId)
  }, [shouldLoadThumbnail, loadVNZImageThumbnail, vnzImage])

  // Build the CloudImage inside the cell from this entity's own state so a
  // loaded thumbnail shows immediately, without waiting for a parent re-render.
  const image = useMemo<CloudImage>(
    () => ({
      id,
      provider: "verizon-cloud",
      url: vnzImage?.fullImageLocalUrl ?? "",
      thumbnailUrl: thumbnailLocalUrl ?? "",
    }),
    [id, vnzImage?.fullImageLocalUrl, thumbnailLocalUrl]
  )

  if (hasErrorLoadingThumbnail) {
    return <CloudImageBrowserImageBroken />
  }

  return (
    <Box position="relative" height="165px">
      {canShowImage && (
        <CloudImageBrowserImage
          image={image}
          isSelected={isSelected}
          onImageSelect={() => onImageSelect(id)}
        />
      )}
      {!canShowImage && (
        <Skeleton width="100%" height="100%" borderRadius="8px" />
      )}
      {shouldShowVideoOverlay && (
        <>
          <Flex
            position="absolute"
            top="0"
            left="0"
            width="100%"
            height="100%"
            backgroundColor="rgba(0, 0, 0, 0.6)"
            borderRadius="8px"
            cursor="not-allowed"
            className="vnz-cloud-video-overlay"
            onClick={() => {
              toast({
                title: t(
                  `components.cloud-provider.vnz.browser.VNZImageSelector.error.unsupportedFileType`
                ),
                status: "error",
                duration: 3000,
                isClosable: true,
                variant: "left-accent",
              })
            }}
          />
          <Icon
            as={BiMoviePlay}
            position="absolute"
            bottom={0}
            right={0}
            transform="translate(-50%, -50%)"
            boxSize={6}
            color="white"
          />
        </>
      )}
    </Box>
  )
}

export default React.memo(VNZImageSelector)
